/**
 * OutreachLines — outreaches per day, one SVG line per contact method, with a
 * legend that toggles each method and a crosshair that reads them all for one
 * day at a time.
 *
 * Extends the hand-rolled SVG pattern `AdditionsLine` established; there is no
 * charting library in this repo and this widget does not add one.
 *
 * The backend hands over a CONTIGUOUS day series with zero-filled gaps, which
 * is what makes a line chart honest here — plotting only the days that had
 * activity would draw a straight run across a quiet fortnight and imply steady
 * contact that never happened. `by_method` is deliberately sparse inside each
 * day (a method with no outreach that day is absent, not zero), so this
 * component reads a missing key as 0 rather than as a gap in the line — and the
 * tooltip prints that 0 rather than skipping the row. On the real search this
 * chart was validated against there is NO outreach at all in April or May; a
 * tooltip that went blank across those two months would undo the entire reason
 * the zero-fill exists.
 *
 * THE PER-METHOD TOGGLES ARE NEW (2026-08-20). This chart shipped with a
 * read-only legend while `StageLines` next to it had working toggles — the two
 * now share `SeriesLegend`, so there is one affordance, one keyboard behaviour
 * and one "everything is hidden" state rather than two that drift.
 *
 * The y-axis is scaled to the busiest single METHOD-day, not to the busiest
 * day's total: with LinkedIn at 1673 against Phone's 4 on a real search, a
 * total-scaled axis would flatten three of the four lines onto the baseline.
 * The legend carries each method's own total, so nothing is hidden by the
 * rescale — only the ink is. Hiding a method rescales the axis to what is left,
 * which is the whole point of being able to hide one.
 *
 * Design-system token utilities only; no inline `style`; English copy only.
 */

import type { SearchReportDay } from "@nu-assist/contracts";
import { useMemo } from "react";

import { methodFillClass, methodStrokeClass } from "../../../../components/stages/chartPalette";
import { ChartTooltip, type ChartTooltipRow } from "./ChartTooltip";
import { SeriesLegend, allHiddenMessage, useSeriesToggles } from "./SeriesLegend";
import { SeriesSwatch } from "./SeriesSwatch";
import { dayLabel, labelStride, monthLabel, monthTicks } from "./dayAxis";
import { FALLBACK_CHART_WIDTH, useElementWidth } from "./useElementWidth";
import { type PlotGeometry, nearestSeriesKey, useNearestPoint } from "./useNearestPoint";

export interface OutreachLinesProps {
  days: readonly SearchReportDay[];
  /** Shown when the search has no outreach at all. */
  emptyMessage: string;
  /**
   * Prefix for every test hook here. Defaults to `outreach`, which is what both
   * tabs use — they never mount at the same time.
   */
  testId?: string;
  /** How the tooltip names a point; the Search Progress tab passes a bucket-aware one. */
  pointLabel?: (day: string) => string;
  /** What one x-axis step IS, for the summary line: `day`, `week`, `month`. */
  pointNoun?: string;
  /**
   * False renders a picture instead of an instrument: no focusable surface, no
   * crosshair, no tooltip, no live region, and a legend of plain text. For the
   * downloaded report, which runs no JavaScript — every one of those affordances
   * would be inert markup inviting an interaction that cannot happen.
   *
   * The per-point figures are the thing a static chart genuinely loses. The
   * legend still carries each series' window total, and the document's tables
   * carry the rest.
   */
  interactive?: boolean;
  /**
   * Overrides the measured width.
   *
   * `useElementWidth` measures in an effect, and `renderToStaticMarkup` runs no
   * effects — so a static render would fall back to `FALLBACK_CHART_WIDTH` and then
   * scale by `containerWidth / 480` in whatever browser opened the file,
   * re-creating the very font-scaling bug the measured width exists to fix. The
   * export passes the width its layout actually uses.
   */
  width?: number;
}

const HEIGHT = 180;
const PAD_LEFT = 8;
const PAD_RIGHT = 8;
const PAD_TOP = 18;
const PAD_BOTTOM = 26;
const PLOT_HEIGHT = HEIGHT - PAD_TOP - PAD_BOTTOM;

/** One method's totals across the window, biggest contributor first. */
export interface MethodSeries {
  method: string;
  total: number;
  counts: number[];
}

/**
 * Fold the day buckets into one series per contact method.
 *
 * Exported for its own test: this is the only real logic in the file, and
 * asserting the shape of an SVG path to check it would be far more brittle than
 * calling the function. Methods are ordered by their own total (desc, then name
 * asc) so the legend reads as a ranking and does not reshuffle between renders.
 */
export function methodSeriesFrom(days: readonly SearchReportDay[]): MethodSeries[] {
  const methods = new Set<string>();
  for (const day of days) {
    for (const method of Object.keys(day.by_method ?? {})) methods.add(method);
  }
  return [...methods]
    .map((method) => {
      const counts = days.map((day) => day.by_method?.[method] ?? 0);
      return {
        method,
        counts,
        total: counts.reduce((sum, n) => sum + n, 0),
      };
    })
    .sort((a, b) => b.total - a.total || a.method.localeCompare(b.method));
}

export function OutreachLines({
  days,
  emptyMessage,
  testId = "outreach",
  pointLabel = dayLabel,
  pointNoun = "day",
  interactive = true,
  width: widthOverride,
}: OutreachLinesProps) {
  // Folded BEFORE the toggles, which need the key set: `useSeriesToggles` derives
  // the hidden set per key rather than snapshotting it at mount.
  const series = methodSeriesFrom(days);
  // No default-off rule here, and that is a decision rather than an omission.
  // These series are CONTACT METHODS, not candidacy stages; running them through
  // `seriesDefaults.stageHiddenByDefault` would find every one of them off the
  // recruiting ladder and open this chart with nothing drawn. The rule reaches
  // "each chart" in Jared's sense — every chart that draws stages applies it, and
  // this one has no stage to deactivate. See `seriesDefaults.ts`.
  const { hidden, toggle } = useSeriesToggles(series.map((s) => s.method));
  const surface = useElementWidth(FALLBACK_CHART_WIDTH);
  // `widthOverride` wins over the measurement: a static render has no layout to
  // measure and `renderToStaticMarkup` runs no effects, so the export passes the
  // width its own document uses. Live pages pass nothing and measure.
  const width = widthOverride ?? surface.width;
  // Memoized on the measured width — see the note in `StageLines`.
  const geometry = useMemo<PlotGeometry>(
    () => ({ width, padLeft: PAD_LEFT, padRight: PAD_RIGHT, height: HEIGHT }),
    [width]
  );
  const crosshair = useNearestPoint(days.length, geometry);
  const plotWidth = width - PAD_LEFT - PAD_RIGHT;

  // Both conditions matter. No days is a search with no outreach at all; days
  // with no methods is a shape the API should not produce, and drawing a bare
  // axis for it would read as "we lost your data" rather than "there is none".
  if (days.length === 0 || series.length === 0) {
    return (
      <p data-testid={`${testId}-empty`} className="text-body text-text-secondary">
        {emptyMessage}
      </p>
    );
  }

  const visible = series.filter((s) => !hidden.has(s.method));
  const peak = Math.max(1, ...visible.flatMap((s) => s.counts));
  const step = days.length > 1 ? plotWidth / (days.length - 1) : 0;
  const x = (index: number) => PAD_LEFT + index * step + (days.length > 1 ? 0 : plotWidth / 2);
  const y = (count: number) => PAD_TOP + PLOT_HEIGHT - (count / peak) * PLOT_HEIGHT;

  // One label per month boundary rather than every Nth day: a 366-day window
  // labelled by index lands on arbitrary dates, and "Mar" under the March
  // section is what a reader is actually looking for.
  const ticks = monthTicks(days.map((day) => day.day));
  // Derived from the width — see the note in `StageLines`.
  const labelEvery = labelStride(ticks.length, plotWidth);

  // The VISIBLE total, so the sentence agrees with the ink above it. With every
  // method shown this is the same number it always was.
  const total = visible.reduce((sum, s) => sum + s.total, 0);

  const focused = interactive ? crosshair.index : null;
  // WHICH LINE THE READER IS LOOKING AT. The bubble stays a whole-point
  // crosshair listing every visible series — that is one read of one question —
  // and this picks the series whose plotted point sits closest to the pointer,
  // so the row describing the line under the cursor is the one emphasised.
  //
  // Nearest is nearest: there is deliberately no distance cap. A cap would leave
  // a reader hovering slightly off every line with an un-highlighted tooltip and
  // no way to tell why. Null on a keyboard focus, because there is no pointer.
  // The nearest VISIBLE series to the pointer, so the row describing the line
  // under the cursor is the one emphasised. Shared with the other line chart —
  // this was nine duplicated lines carrying two `as number` casts each, because
  // the null check above them did not narrow into the closure.
  // `focused === null` short-circuits BEFORE the call rather than being encoded
  // as an empty array argument. Same result, but `focused` is then narrowed to a
  // number inside the closure — so the invariant is held by the type rather than
  // by two files agreeing to pass `[]` and read `focused ?? 0` (PR #752, N3).
  const highlightKey =
    focused === null
      ? undefined
      : nearestSeriesKey(
          visible,
          crosshair.pointerY,
          (s) => s.method,
          (s) => y(s.counts[focused] ?? 0)
        );

  const tooltipRows: ChartTooltipRow[] =
    focused === null
      ? []
      : visible.map((s) => ({
          key: s.method,
          label: s.method,
          value: String(s.counts[focused] ?? 0),
          swatch: <SeriesSwatch strokeClass={methodStrokeClass(s.method)} />,
        }));

  return (
    <div data-testid={`${testId}-lines`}>
      {/* THE FOCUSABLE SURFACE. Two suppressions below, one prose reason for
          both: this div is the chart's interactive surface, and biome has no
          rule that knows charts exist.

          `noNoninteractiveTabindex` — the per-day figures are in no other DOM
          node on the page, so a chart that cannot take focus withholds them from
          every reader who does not use a pointer (FR1.5). `@nu-assist/ui`'s
          `Tooltip` makes the same call for the same reason.

          `useSemanticElements` — the element biome wants for `role="group"` is
          `<fieldset>`, which announces its contents as a grouping of form
          controls and pulls `<legend>` semantics in with it. There is no
          semantic element for "a plot you walk with the arrow keys"; the role,
          the label and the key handling below ARE the accessible-chart
          pattern. */}
      <div
        // NOTE: these two used to carry `biome-ignore` directives. They stopped
        // being needed when `tabIndex` and `role` became EXPRESSIONS — biome's a11y
        // rules only fire on literals. The reasoning is kept because it is still
        // true, and because hardcoding either value again brings the rules back.
        // the chart IS the interactive surface — see the note above
        tabIndex={interactive ? 0 : undefined}
        // <fieldset> would announce a chart as a form-control group — see the note above
        role={interactive ? "group" : undefined}
        aria-label={
          interactive
            ? `Outreaches by ${pointNoun}, by method. Use the left and right arrow keys to read each ${pointNoun}, Home and End for the ends.`
            : undefined
        }
        data-testid={`${testId}-surface`}
        ref={surface.ref}
        className="relative rounded-xs focus:outline-none focus:ring-1 focus:ring-accent"
        {...(interactive ? crosshair.surfaceProps : {})}
      >
        <svg
          viewBox={`0 0 ${width} ${HEIGHT}`}
          role="presentation"
          aria-hidden="true"
          className="block w-full"
        >
          {/* Behind the series, so a line is never hidden under the crosshair. */}
          {focused !== null ? (
            <line
              x1={x(focused)}
              y1={PAD_TOP}
              x2={x(focused)}
              y2={PAD_TOP + PLOT_HEIGHT}
              className="stroke-text-secondary"
              strokeWidth={0.75}
              strokeDasharray="2 2"
            />
          ) : null}
          {visible.map((s) => (
            <polyline
              key={s.method}
              points={s.counts.map((count, i) => `${x(i)},${y(count)}`).join(" ")}
              fill="none"
              strokeWidth={1.5}
              strokeLinejoin="round"
              className={methodStrokeClass(s.method)}
            />
          ))}
          {focused !== null
            ? visible.map((s) => (
                <circle
                  key={s.method}
                  cx={x(focused)}
                  cy={y(s.counts[focused] ?? 0)}
                  // See `StageLines`: the highlighted marker grows.
                  r={s.method === highlightKey ? 4 : 2.5}
                  className={methodFillClass(s.method)}
                />
              ))
            : null}
          {ticks.map((tick, i) =>
            i % labelEvery === 0 ? (
              <text
                key={tick.day}
                x={x(tick.index)}
                y={HEIGHT - 8}
                // Outer ticks anchor to the PLOT, not to their own centre.
                // The first point sits at PAD_LEFT (8) in a 480-unit viewBox,
                // and January's label carries a year ("Jan 26"), so a centred
                // first tick overflows the left edge and renders clipped as
                // "an 26" — reported on real data whose first day is
                // 2026-01-30. Found by /pr-review iter 1, cause pinned at
                // iter 2.
                textAnchor={i === 0 ? "start" : i === ticks.length - 1 ? "end" : "middle"}
                className="fill-text-secondary text-nano"
              >
                {monthLabel(tick.day)}
              </text>
            ) : null
          )}
        </svg>

        {focused !== null ? (
          <ChartTooltip
            testId={`${testId}-tooltip`}
            title={pointLabel(days[focused].day)}
            rows={tooltipRows}
            side={crosshair.side}
            highlightKey={highlightKey}
            emptyLabel={allHiddenMessage("method")}
          />
        ) : null}
      </div>

      {/* The keyboard reader's copy of the crosshair — fed only on a key press,
          so a pointer sweep across a year does not announce 366 times. */}
      {interactive ? (
        <output data-testid={`${testId}-readout`} className="sr-only">
          {focused !== null && crosshair.fromKeyboard
            ? `${pointLabel(days[focused].day)}: ${
                tooltipRows.length === 0
                  ? allHiddenMessage("method")
                  : tooltipRows.map((row) => `${row.label} ${row.value}`).join(", ")
              }`
            : ""}
        </output>
      ) : null}

      {/* Also the accessible representation — the SVG is decorative — and where
          each method's real total lives, since the y-axis is rescaled to the
          busiest method-day. */}
      <SeriesLegend
        entries={series.map((s) => ({
          key: s.method,
          label: s.method,
          total: s.total,
          strokeClass: methodStrokeClass(s.method),
        }))}
        hidden={hidden}
        onToggle={toggle}
        interactive={interactive}
        testId={testId}
      />

      <p data-testid={`${testId}-summary`} className="mt-1 text-caption text-text-secondary">
        {visible.length === 0
          ? allHiddenMessage("method")
          : `${total} outreach${total === 1 ? "" : "es"} over ${days.length} ${pointNoun}${
              days.length === 1 ? "" : "s"
            }${
              visible.length === series.length
                ? ""
                : `, ${visible.length} of ${series.length} methods shown`
            }`}
      </p>
    </div>
  );
}
